import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { redirectToLogin } from "@/lib/auth-session";
import { fetchPlatformSummary } from "@/lib/platform-api";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { API_BASE } from "@/lib/utils";

export default function PlatformOverview() {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [aiHealth, setAiHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPlatformSummary();
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled) {
          const msg = err.message || "Failed to load summary";
          setError(msg);
          if (/unauthorized|not signed in|session expired|invalid or expired token/i.test(msg)) {
            redirectToLogin("expired");
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/ai/health`, { headers: getAuthHeaders(), cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAiHealth(data);
      } catch {
        if (!cancelled) setAiHealth(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="p-8 lg:p-12 max-w-[1100px] mx-auto">
      <div className="text-overline">Platform</div>
      <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Operator overview</h1>
      <p className="mt-3 text-sm text-[var(--text-secondary)] max-w-2xl leading-relaxed">
        Track customer workspaces, trials, and leads. Therme (and all live EMS data) lives on tenant{" "}
        <code className="font-mono text-xs">{summary?.productionTenantId || "default"}</code> — enterprise
        tier, grandfathered, no expiry.
      </p>
      {error ? <p className="mt-4 text-sm text-red-400">{error}</p> : null}
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Accounts", value: summary?.accountCount ?? "—" },
          { label: "Active", value: summary?.activeCount ?? "—" },
          { label: "Trials", value: summary?.trialCount ?? "—" },
          { label: "Leads", value: summary?.leadCount ?? "—" },
          { label: "Qualified leads", value: summary?.qualifiedLeadCount ?? "—" },
          { label: "Trials expiring (3d)", value: summary?.expiringTrials ?? "—", highlight: (summary?.expiringTrials || 0) > 0 },
          { label: "Overdue invoices", value: summary?.overdueInvoices ?? "—", highlight: (summary?.overdueInvoices || 0) > 0 },
          {
            label: "Billing queue",
            value: summary?.pendingBillingRequests ?? "—",
            highlight: (summary?.pendingBillingRequests || 0) > 0,
          },
        ].map((kpi) => (
          <div key={kpi.label} className="surface p-5">
            <div className="text-overline">{kpi.label}</div>
            <div
              className={`font-heading text-3xl mt-2 ${kpi.highlight ? "text-[var(--primary)]" : ""}`}
            >
              {kpi.value}
            </div>
          </div>
        ))}
        <div className="surface p-5 sm:col-span-2" data-testid="platform-ai-health">
          <div className="text-overline">AivoryONE / AI stack</div>
          <div className={`font-heading text-3xl mt-2 capitalize ${aiHealth?.status === "ok" ? "text-emerald-400" : aiHealth ? "text-amber-400" : ""}`}>
            {aiHealth?.status || "—"}
          </div>
          <p className="text-xs text-[var(--text-tertiary)] mt-2 font-mono">
            embed {aiHealth?.embed?.ok ? "ok" : "—"} · qdrant {aiHealth?.qdrant?.ok ? "ok" : "—"} · llm {aiHealth?.llm?.ok ? "ok" : "—"}
            {aiHealth?.mockMode ? " · mock" : ""}
          </p>
        </div>
      </div>
      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/platform/trial-invites" className="btn-primary">
          Invite trial
        </Link>
        <Link to="/platform/accounts" className="btn-secondary">
          Manage accounts
        </Link>
        <Link to="/platform/billing" className="btn-secondary">
          Billing queue
        </Link>
        <Link to="/platform/billing/settings" className="btn-secondary">
          Revolut settings
        </Link>
        <Link to="/platform/landing" className="btn-secondary">
          Edit homepage
        </Link>
        <Link to="/platform/leads" className="btn-secondary">
          View leads
        </Link>
      </div>
    </div>
  );
}
